/* hydrus-cart-drawer.js
 * Companion to snippets/hydrus-cart-drawer.liquid.
 *
 * Document-level event delegation so handlers survive the innerHTML
 * swap the legacy Shopify section-refresh performs on every cart mutation.
 * Singleton guard prevents double-binding across section re-renders.
 *
 * Mutation flow:
 *   1. User action (qty change, remove, xsell-add).
 *   2. POST to /cart/{change,add}.js.
 *   3. Dispatch `cart:refresh` event → legacy app.js CartDrawer.refresh()
 *      re-fetches `?sections=cart-drawer,cart-bubble` and swaps the HTML.
 *      This preserves the existing PDP-ATC → drawer-refresh contract.
 *
 * Open/close/overlay/focus-trap all stay owned by app.js's CartDrawer
 * custom element + OverlayManager. This file does not touch those.
 */
(function () {
  if (window.HydrusCartDrawer) return;
  window.HydrusCartDrawer = true;

  const DEBOUNCE_MS = 350;

  function getDrawer() {
    return document.getElementById('Cart-Drawer');
  }

  function setBusy(busy) {
    const drawer = getDrawer();
    if (drawer) drawer.setAttribute('data-hydrus-drawer-updating', busy ? 'true' : 'false');
  }

  function refreshCart() {
    // Legacy app.js CartDrawer listens for `cart:refresh` on document. It
    // fetches ?sections=cart-drawer,cart-bubble and innerHTML-swaps the
    // `.cart-drawer` contents. Also updates the cart bubble count.
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    // Clear the busy overlay on the next tick — the legacy refresh
    // completes async and replaces our state attribute anyway.
    setTimeout(() => setBusy(false), 600);
  }

  // ---------- Quantity (debounced per-key) ----------
  const qtyTimers = new Map();

  async function updateQuantity(key, quantity) {
    setBusy(true);
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
    } catch (err) {
      console.error('[HydrusCartDrawer] qty update failed', err);
    }
    refreshCart();
  }

  function scheduleQtyUpdate(key, quantity) {
    if (qtyTimers.has(key)) clearTimeout(qtyTimers.get(key));
    qtyTimers.set(
      key,
      setTimeout(() => {
        qtyTimers.delete(key);
        updateQuantity(key, quantity);
      }, DEBOUNCE_MS)
    );
  }

  // ---------- Remove ----------
  async function removeItem(key) {
    setBusy(true);
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 }),
      });
    } catch (err) {
      console.error('[HydrusCartDrawer] remove failed', err);
    }
    refreshCart();
  }

  // ---------- Cross-sell add ----------
  async function addXsell(variantId, btn) {
    if (!variantId) return;
    if (btn) btn.setAttribute('aria-busy', 'true');
    setBusy(true);
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });
      if (!res.ok) {
        console.warn('[HydrusCartDrawer] xsell add rejected');
        if (btn) btn.setAttribute('aria-busy', 'false');
        setBusy(false);
        return;
      }
      if (btn) {
        btn.setAttribute('data-added', 'true');
        btn.textContent = 'Added';
      }
    } catch (err) {
      console.error('[HydrusCartDrawer] xsell add failed', err);
    }
    refreshCart();
  }

  // ---------- Event delegation ----------
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-hydrus-drawer-action]');
    if (!btn) return;
    // Scope to elements inside the cart drawer — don't clash with the
    // cart-page delegation listener which uses data-hydrus-cart-action.
    if (!btn.closest('#Cart-Drawer')) return;

    const action = btn.getAttribute('data-hydrus-drawer-action');

    if (action === 'remove') {
      e.preventDefault();
      const key = btn.getAttribute('data-key');
      if (key) removeItem(key);
      return;
    }

    if (action === 'increment' || action === 'decrement') {
      e.preventDefault();
      const wrap = btn.closest('[data-hydrus-drawer-qty]');
      if (!wrap) return;
      const input = wrap.querySelector('[data-hydrus-drawer-input]');
      if (!input) return;
      const step = Number(input.step || 1);
      const min = Number(input.min || 1);
      const max = input.max ? Number(input.max) : Infinity;
      let next = Number(input.value || 1) + (action === 'increment' ? step : -step);
      if (next < min) next = min;
      if (next > max) next = max;
      input.value = next;
      scheduleQtyUpdate(input.getAttribute('data-key'), next);
      return;
    }

    if (action === 'xsell-add') {
      e.preventDefault();
      addXsell(btn.getAttribute('data-variant-id'), btn);
      return;
    }
  });

  document.addEventListener('input', (e) => {
    const input = e.target.closest('[data-hydrus-drawer-input]');
    if (!input) return;
    if (!input.closest('#Cart-Drawer')) return;
    const key = input.getAttribute('data-key');
    const value = Number(input.value);
    if (!key || !Number.isFinite(value) || value < 0) return;
    scheduleQtyUpdate(key, value);
  });
})();
